import React, { Component, ErrorInfo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Modal, Space, Typography, message } from 'antd';

import { faFaceFrown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const { Paragraph, Text } = Typography;

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
}

interface FallbackProps {
    error?: Error;
    errorInfo?: ErrorInfo;
    onReset: () => void;
}

// Функциональная обёртка нужна только чтобы получить доступ к useNavigate —
// сам ErrorBoundary обязан быть классом (getDerivedStateFromError/componentDidCatch
// есть только у классовых компонентов).
const ErrorBoundaryFallback: React.FC<FallbackProps> = ({ error, errorInfo, onReset }) => {
    const navigate = useNavigate();
    const [detailsOpen, setDetailsOpen] = useState(false);

    const handleBackToProfile = () => {
        // Внутри react-native-webview host перехватывает переходы по <a href>
        // как внешнюю ссылку и открывает системный браузер вместо навигации
        // в приложении. Поэтому уходим на /lk через SPA-роутер (pushState),
        // а не через обычную ссылку — такой переход WebView вообще не видит.
        onReset();
        navigate('/lk');
    };

    return (
        <div id='apelsin-app-content'>
            <h2 style={{marginTop: '100px', textAlign: 'center'}}>
                Что-то пошло не так... <FontAwesomeIcon icon={faFaceFrown} color="orange" />
            </h2>
            <Space style={{width: '100%', justifyContent: 'center'}}>
                <Button type='link' onClick={handleBackToProfile}>Вернуться на страницу профиля</Button>
                <Button type='link' onClick={() => setDetailsOpen(true)}>Подробнее</Button>
            </Space>
            <Modal
                title='Подробности ошибки'
                open={detailsOpen}
                onCancel={() => setDetailsOpen(false)}
                footer={null}
                width={800}
            >
                <Paragraph copyable={{ text: `${error?.name}: ${error?.message}\n\n${error?.stack ?? ''}${errorInfo?.componentStack ? `\n\nComponent stack:${errorInfo.componentStack}` : ''}` }}>
                    <Text strong>{error?.name}: </Text>
                    <Text>{error?.message}</Text>
                </Paragraph>
                {error?.stack && (
                    <pre style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12}}>
                        {error.stack}
                    </pre>
                )}
                {errorInfo?.componentStack && (
                    <>
                        <Text strong>Component stack:</Text>
                        <pre style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12}}>
                            {errorInfo.componentStack}
                        </pre>
                    </>
                )}
            </Modal>
        </div>
    );
};

class ErrorBoundary extends Component<{ children: React.ReactNode }, ErrorBoundaryState> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Caught an error:', error, errorInfo);
        message.error('Ошибка на стороне клиента');
        this.setState({ errorInfo });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    };

    render() {
        if (this.state.hasError) {
            return (
                <ErrorBoundaryFallback
                    error={this.state.error}
                    errorInfo={this.state.errorInfo}
                    onReset={this.handleReset}
                />
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
